// components/TopBar.js
/**
 * Mobile Responsive TopBar Component
 * 
 * Mobile Adjustments (Ultra-Compact):
 * - TopBar height: 2.25rem (36px) on mobile
 * - Logo size: 28px on mobile
 * - Container scale: 60% on mobile
 * - Auto-hide on scroll down, show on scroll up
 * 
 * Note: All buttons have been moved to Navbar component
 */
import React, { useState, useEffect, useContext } from 'react';
import { Slide } from '@mui/material';
import ChainedLM from '../assets/ChainedLM.png';
import ChainedLM_mobile from '../assets/ChainedLM_mobile.png';
import SlidingSlidebar from './SlidingSlidebar';
import { AppContextisChecked } from '../context/AppContextisChecked';
import Navbar from './Navbar';

const TopBar = ({ setIsChecked: setParentIsChecked }) => {
  const { isChecked: contextIsChecked, setIsChecked: setContextIsChecked } = useContext(AppContextisChecked);
  const [localIsChecked, setLocalIsChecked] = useState(() => {
    try {
      const storedValue = localStorage.getItem('isChecked');
      return storedValue !== null ? JSON.parse(storedValue) : false;
    } catch (e) {
      console.error('Error parsing isChecked from localStorage:', e);
      return false;
    }
  });
  
  // State for mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  // State for scroll behavior
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // State to control tooltip visibility
  const [operatorTooltipVisible, setOperatorTooltipVisible] = useState(false);
  const [researchTooltipVisible, setResearchTooltipVisible] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle scroll behavior for mobile
  useEffect(() => {
    if (!isMobile) {
      setIsVisible(true);
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show topbar when at the top of the page
      if (currentScrollY <= 10) {
        setIsVisible(true);
      }
      // Show topbar when scrolling up
      else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }
      // Hide topbar when scrolling down
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollListener);
    return () => window.removeEventListener('scroll', scrollListener);
  }, [lastScrollY, isMobile]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'isChecked') {
        try {
          const newValue = event.newValue !== null ? JSON.parse(event.newValue) : false;
          setLocalIsChecked(newValue);
          setParentIsChecked(newValue);
          setContextIsChecked(newValue);
          // console.log('TopBar: isChecked updated from storage event:', newValue);
        } catch (error) {
          console.error('TopBar: Error parsing isChecked from storage event:', error);
          setLocalIsChecked(false);
          setParentIsChecked(false);
          setContextIsChecked(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [setParentIsChecked, setContextIsChecked]);

  useEffect(() => {
    if (localIsChecked !== contextIsChecked) {
      try {
        localStorage.setItem('isChecked', JSON.stringify(contextIsChecked));
        setLocalIsChecked(contextIsChecked);
        setParentIsChecked(contextIsChecked);
        // console.log('TopBar: localIsChecked synced with contextIsChecked:', contextIsChecked);
      } catch (error) {
        console.error("Error setting isChecked in localStorage:", error);
      }
    }
  }, [contextIsChecked, setParentIsChecked]);

  // Responsive styles
  const topBarStyle = {
    position: isMobile ? 'fixed' : 'fixed',
    top: isVisible ? 0 : '-100px',
    left: 0,
    right: 0,
    height: isMobile ? '2.25rem' : '2.67rem',
    background: 'linear-gradient(45deg, rgb(255, 255, 255), rgb(48 46 46 / 65%))',
    borderBottom: '1px solid rgb(229, 231, 235)',
    display: 'flex',
    alignItems: 'center',
    paddingRight: isMobile ? '0.25rem' : '1.5rem',
    paddingLeft: isMobile ? '0.25rem' : '0',
    zIndex: 5,
    transition: 'top 0.3s ease-in-out',
  };

  const logoStyle = {
    height: isMobile ? '28px' : '43px',
    marginLeft: isMobile ? '2px' : '0px',
    borderRadius: '0%',
  };

  const containerStyle = { 
    position: 'relative', 
    display: 'inline-flex', 
    alignItems: 'center', 
    borderRadius: isMobile ? '10px' : '20px', 
    overflow: 'hidden', 
    border: '1px solid #333', 
    borderColor: 'rgba(145, 148, 153, 0.84)', 
    backgroundColor: '#1f1f1f', 
    fontFamily: 'sans-serif', 
    textAlign: 'center', 
    marginLeft: 'auto',
    transform: isMobile ? 'scale(0.6)' : 'scale(1)',
    transformOrigin: 'center right',
  };
  
  const baseButtonStyle = { 
    padding: isMobile ? '2px 5px' : '6px 16px',
    cursor: 'pointer', 
    userSelect: 'none', 
    transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
    position: 'relative',
    fontSize: isMobile ? '8px' : '14px',
    fontWeight: isMobile ? '600' : 'normal',
    letterSpacing: isMobile ? '0.3px' : 'normal',
  };
  
  const planStyle = { 
    ...baseButtonStyle, 
    backgroundColor: localIsChecked ? '#f0c600' : 'transparent', 
    color: localIsChecked ? 'white' : 'white' 
  };
  
  const actStyle = { 
    ...baseButtonStyle, 
    backgroundColor: localIsChecked ? 'transparent' : '#2196f3', 
    color: localIsChecked ? 'white' : 'white' 
  };

  // Tooltip styles - positioned outside the container
  const tooltipStyle = {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: isMobile ? '2px' : '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    color: 'white',
    padding: isMobile ? '1px 4px' : '6px 10px',
    borderRadius: '4px',
    fontSize: isMobile ? '7px' : '12px',
    whiteSpace: 'nowrap',
    zIndex: 100,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
    transition: 'opacity 0.2s, visibility 0.2s',
    opacity: 0,
    visibility: 'hidden',
    pointerEvents: 'none'
  };

  // Active tooltip style
  const activeTooltipStyle = {
    ...tooltipStyle,
    opacity: 1,
    visibility: 'visible'
  };

  return (
    <div style={topBarStyle}>
      <h1 style={{ 
        fontSize: isMobile ? '1rem' : '1.5rem', 
        fontFamily: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif', 
        color: 'rgb(17 24 39)', 
        letterSpacing: '-0.025em',
        display: isMobile ? 'none' : 'block'
      }}>
        {/* ChainedLLM */}
      </h1>
      
      {/* Single merged sidebar: chat history + settings live in SlidingSlidebar. */}
      <SlidingSlidebar />

      {/* Use desktop logo only for desktop view */}
      {!isMobile && <img 
        style={logoStyle} 
        src={ChainedLM}  
        alt="RAGRecruitLegal Logo" 
      />}
      
      {/* Navbar handles all buttons now, including mobile icons */}
      <Navbar isMobile={isMobile} />
    </div>
  );
};

export default TopBar;